import {
  B2bCampaignActionHandler,
  B2bContract,
  B2bContractStatus,
  B2bContractType,
  B2bDaysIncrementStep,
  B2bMatchingContributionPaymentFrequency,
  B2bPaperTransactionStatementFrequency,
  B2bTeam,
} from '@Common/types/bo/b2b/contracts';
import { B2bContractsDocumentsType } from '@Common/types/bo/b2b/contracts/documents';

import { TranslationKeys } from '@sh4res/bo-common/i18n/types';
import { makeChoicesCommon } from '@sh4res/bo-common/utils';

export const contractRelationshipTypeChoices = makeChoicesCommon<
  B2bContract['relationship_type']
>()({
  direct: 'resources.bo_common.b2b.contracts.choices.relationship_type.direct',
  indirect: 'resources.bo_common.b2b.contracts.choices.relationship_type.indirect',
});

export const b2bMatchingContributionPaymentFrequencyChoices =
  makeChoicesCommon<B2bMatchingContributionPaymentFrequency>()({
    '1_month':
      'resources.bo_common.b2b.contracts.choices.matching_contribution_payment_frequency.1_month',
    '3_months':
      'resources.bo_common.b2b.contracts.choices.matching_contribution_payment_frequency.3_months',
    '6_months':
      'resources.bo_common.b2b.contracts.choices.matching_contribution_payment_frequency.6_months',
    '12_months':
      'resources.bo_common.b2b.contracts.choices.matching_contribution_payment_frequency.12_months',
  });

export const contractCampaignActionHandlerChoices = makeChoicesCommon<B2bCampaignActionHandler>()({
  distributor: 'resources.bo_common.b2b.contracts.choices.campaign_action_handler.distributor',
  company: 'resources.bo_common.b2b.contracts.choices.campaign_action_handler.company',
});

export const contractDaysIncrementStepChoices: Array<{
  id: B2bDaysIncrementStep;
  name: TranslationKeys;
}> = [
  {
    id: 0.25,
    name: 'resources.bo_common.b2b.contracts.choices.days_increment_step.quarter_day',
  },
  {
    id: 0.5,
    name: 'resources.bo_common.b2b.contracts.choices.days_increment_step.half_day',
  },
];

// Only company/company, company/distributor, and distributor/distributor are valid pairings of
// allocation_computed_by and choice_collected_by (see isValidAllocationChoiceCombination).
export function contractAllocationChoicesFor(
  allocationComputedBy: B2bCampaignActionHandler | null | undefined,
) {
  return allocationComputedBy === 'distributor'
    ? contractCampaignActionHandlerChoices.filter((choice) => choice.id === 'distributor')
    : contractCampaignActionHandlerChoices;
}

export const contractTeamChoices = makeChoicesCommon<B2bTeam>()({
  standard: 'resources.bo_common.b2b.contracts.choices.team.standard',
  middle: 'resources.bo_common.b2b.contracts.choices.team.middle',
  key_account: 'resources.bo_common.b2b.contracts.choices.team.key_account',
  international: 'resources.bo_common.b2b.contracts.choices.team.international',
});

export const contractTypeChoices = makeChoicesCommon<B2bContractType>()({
  soles: 'resources.bo_common.b2b.contracts.choices.contract_type.soles',
  es_custom: 'resources.bo_common.b2b.contracts.choices.contract_type.es_custom',
  er_standard: 'resources.bo_common.b2b.contracts.choices.contract_type.er_standard',
  er_custom: 'resources.bo_common.b2b.contracts.choices.contract_type.er_custom',
  er_external: 'resources.bo_common.b2b.contracts.choices.contract_type.er_external',
  essentiel: 'resources.bo_common.b2b.contracts.choices.contract_type.essentiel',
  flexible: 'resources.bo_common.b2b.contracts.choices.contract_type.flexible',
  patrimonial: 'resources.bo_common.b2b.contracts.choices.contract_type.patrimonial',
  premium: 'resources.bo_common.b2b.contracts.choices.contract_type.premium',
  sur_mesure: 'resources.bo_common.b2b.contracts.choices.contract_type.sur_mesure',
});

export const contractStatusChoices = makeChoicesCommon<B2bContractStatus>()({
  offer: 'resources.bo_common.b2b.contracts.choices.status.offer',
  effective: 'resources.bo_common.b2b.contracts.choices.status.effective',
  suspended: 'resources.bo_common.b2b.contracts.choices.status.suspended',
  denounced: 'resources.bo_common.b2b.contracts.choices.status.denounced',
  process_of_creation: 'resources.bo_common.b2b.contracts.choices.status.process_of_creation',
  closed_by_amendment: 'resources.bo_common.b2b.contracts.choices.status.closed_by_amendment',
  other: 'resources.bo_common.b2b.contracts.choices.status.other',
  terminated: 'resources.bo_common.b2b.contracts.choices.status.terminated',
});

export const paperTransactionStatementFrequencyChoices =
  makeChoicesCommon<B2bPaperTransactionStatementFrequency>()({
    digital_only:
      'resources.bo_common.b2b.contracts.choices.paper_transaction_statement_frequency.digital_only',
    '1_month':
      'resources.bo_common.b2b.contracts.choices.paper_transaction_statement_frequency.1_month',
    '3_months':
      'resources.bo_common.b2b.contracts.choices.paper_transaction_statement_frequency.3_months',
    '6_months':
      'resources.bo_common.b2b.contracts.choices.paper_transaction_statement_frequency.6_months',
    '12_months':
      'resources.bo_common.b2b.contracts.choices.paper_transaction_statement_frequency.12_months',
    every_time:
      'resources.bo_common.b2b.contracts.choices.paper_transaction_statement_frequency.every_time',
  });

export const b2bContractsDocumentsTypesChoices = makeChoicesCommon<B2bContractsDocumentsType>()({
  employee_savings_booklet:
    'resources.bo_common.b2b.contracts.documents.type.employee_savings_booklet',
  general_terms_and_conditions:
    'resources.bo_common.b2b.contracts.documents.type.general_terms_and_conditions',
  plan_regulations: 'resources.bo_common.b2b.contracts.documents.type.plan_regulations',
  amendments: 'resources.bo_common.b2b.contracts.documents.type.amendments',
  price_list_employee: 'resources.bo_common.b2b.contracts.documents.type.price_list_employee',
  price_list_company: 'resources.bo_common.b2b.contracts.documents.type.price_list_company',
  signed_contract_amendment:
    'resources.bo_common.b2b.contracts.documents.type.signed_contract_amendment',
});
